import { useEffect, useState } from 'react';

interface SessionBookingsModalProps {
  sessionId: string;
  activityTitle: string;
  sessionDate: string;
  getToken: () => Promise<string | undefined>;
  onClose: () => void;
  apiPrefix?: 'admin' | 'host';
}

interface CSVRow {
  [key: string]: string;
}

export default function SessionBookingsModal({
  sessionId,
  activityTitle,
  sessionDate,
  getToken,
  onClose,
  apiPrefix = 'admin',
}: SessionBookingsModalProps) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<CSVRow[]>([]);
  const [csvBlob, setCsvBlob] = useState<Blob | null>(null);
  const [filename, setFilename] = useState<string>('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const token = await getToken();
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

        const response = await fetch(`${apiUrl}/${apiPrefix}/sessions/${sessionId}/export`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch data');
        }

        const blob = await response.blob();
        setCsvBlob(blob);

        // Get filename from header
        const contentDisposition = response.headers.get('Content-Disposition');
        const filenameMatch = contentDisposition?.match(/filename="?([^"]+)"?/);
        setFilename(filenameMatch?.[1] || `session-${sessionId}.csv`);

        // Parse CSV
        const text = await blob.text();
        // Remove BOM if present
        const cleanText = text.replace(/^\ufeff/, '');
        const lines = cleanText.split('\n').filter((line) => line.trim());

        if (lines.length > 0) {
          const headerLine = lines[0];
          const parsedHeaders = parseCSVLine(headerLine);
          setHeaders(parsedHeaders);

          const parsedRows: CSVRow[] = [];
          for (let i = 1; i < lines.length; i++) {
            const values = parseCSVLine(lines[i]);
            const row: CSVRow = {};
            parsedHeaders.forEach((header, index) => {
              row[header] = values[index] || '';
            });
            parsedRows.push(row);
          }
          setRows(parsedRows);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [sessionId, getToken, apiPrefix]);

  // Parse CSV line handling quoted fields
  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];

      if (inQuotes) {
        if (char === '"' && nextChar === '"') {
          current += '"';
          i++; // Skip next quote
        } else if (char === '"') {
          inQuotes = false;
        } else {
          current += char;
        }
      } else {
        if (char === '"') {
          inQuotes = true;
        } else if (char === ',') {
          result.push(current);
          current = '';
        } else {
          current += char;
        }
      }
    }
    result.push(current);
    return result;
  };

  const handleExport = () => {
    if (!csvBlob) return;

    const url = window.URL.createObjectURL(csvBlob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  // Column header Chinese mapping
  const headerLabels: Record<string, string> = {
    'Customer Name': '客户姓名',
    'Customer Email': '邮箱',
    Child: '儿童',
    Age: '年龄',
    Notes: '备注',
    Adult: '成人',
    Phone: '电话',
    Photo: '出镜',
    'Booked On': '预订日期',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">{activityTitle}</h2>
            <p className="text-sm text-gray-500">{sessionDate} 报名名单</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              disabled={!csvBlob}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              导出 CSV
            </button>
            <button onClick={onClose} className="p-2 text-gray-400 hover:text-gray-600">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-red-600">{error}</div>
          ) : rows.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-500">暂无预订数据</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {headers.map((header) => (
                      <th
                        key={header}
                        className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap"
                      >
                        {headerLabels[header] || header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {rows.map((row, rowIndex) => (
                    <tr key={rowIndex} className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      {headers.map((header) => (
                        <td
                          key={header}
                          className="px-4 py-3 text-sm text-gray-900 whitespace-nowrap"
                        >
                          {row[header] || ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer */}
        {!loading && !error && rows.length > 0 && (
          <div className="px-6 py-3 bg-gray-50 text-sm text-gray-500">
            共 {rows.filter((r) => r['Customer Name']).length} 组预订
          </div>
        )}
      </div>
    </div>
  );
}
